// tune-helpers.js

export const getAlignment = (block) => {
  return block?.tunes?.AlignmentBlockTune?.alignment ?? null;
};

export const getColor = (block) => {
  return block?.tunes?.FontColorTool?.color ?? block?.data?.color ?? null;
};

export const getAnimation = (block) => {
  return block?.tunes?.Animation?.animation?.class ?? null;
};

export const getAnimationIndex = (block, offset) => {
  const index = block?.tunes?.Animation?.animationIndex;
  if (index == null) return null;
  return offset != null ? index + offset : index;
};

export const getDimensions = (block) => {
  const d = block?.data?.dimensions ?? block?.tunes?.DimensionTune?.dimensions;
  if (!d) return null;
  return {
    width: d.width === "auto" || d.width === "0" ? "auto" : `${d.width}${d.widthUnit}`,
    height: d.height === "auto" || d.height === "0" ? "auto" : `${d.height}${d.heightUnit}`,
    objectFit: "cover",
  };
};

export const getBlockBgColor = (block) => {
  return block?.tunes?.BlockBackgroundTool?.bgColor?.name ?? "blue";
};

export const getBlockPattern = (block) => {
  return block?.tunes?.BlockBackgroundTool?.pattern ?? null;
};

export const getHeadingToggle = (block) => {
  return block?.tunes?.HeadingToggleTool?.variant ?? "heading-title";
};

export const getOverlay = (block) => {
  const overlay = block?.tunes?.OverlayTool;
  if (!overlay) return null;
  return {
    backgroundColor: overlay.color,
    opacity: overlay.opacity,
  };
};

export const getMargins = (block) => {
  const m = block?.tunes?.MarginTool?.margins;
  if (!m) {
    // No margin tune (e.g. migrated content, or a block created without
    // touching the margin tool): fall back to sensible document spacing so
    // the read-only view isn't cramped. The editor supplies its own block
    // spacing, which is why it only shows in view/preview. Headings get space
    // above and a tight gap below; body/list/media get standard spacing below.
    // const isHeading = block?.type === "title" || block?.type === "subtitle";

    return {
      marginTop: 0,
      marginBottom: '30px',
    };
  }
  return {
    marginTop: m.top === "auto" || m.top === "0" ? "auto" : `${m.top}px`,
    marginRight:
      m.right === "auto" || m.right === "0" ? "auto" : `${m.right}px`,
    marginBottom:
      m.bottom === "auto" || m.bottom === "0" ? "auto" : `${m.bottom}px`,
    marginLeft: m.left === "auto" || m.left === "0" ? "auto" : `${m.left}px`,
  };
};

export const getDraggable = (block) => {
  const style = block?.tunes?.DragResize?.draggable;
  if (!style) return null;

  const parse = (value) =>
    typeof value === "string" ? parseInt(value.replace("px", "")) : value;

  if (style.absolute) {
    return {
      position: "absolute",
      top: parse(style.top),
      left: parse(style.left),
      width: parse(style.width),
      height: parse(style.height),
    };
  }

  if (style.width || style.height) {
    return {
      width: parse(style.width),
      height: parse(style.height),
    };
  }

  return null;
};

export const getMediaDraggable = (block, slideLayoutId) => {
  if (
    slideLayoutId !== "custom" ||
    !block.data?.tuneData ||
    typeof block.data.tuneData !== "object"
  )
    return null;

  const tuneData = block.data.tuneData;
  const keys = Object.keys(tuneData);
  if (!keys.length) return null;

  return keys.reduce(
    (acc, key) => {
      if (tuneData[key] !== "") acc[key] = tuneData[key];
      return acc;
    },
    { position: "absolute" },
  );
};

export const getFontSize = (block) => {
  const fontSize = block?.tunes?.FontSizeTune?.fontSize;
  return fontSize ? { fontSize: `${fontSize}px`, lineHeight: "unset" } : null;
};

export const processCellStyle = (
  rowCount,
  rowIndex,
  cellCount,
  cellIndex,
  block,
  slideLayoutId,
) => {
  if (!block.data?.styled || slideLayoutId !== "custom") return null;

  const styled = block.data.styled;
  const {
    tableStyle,
    backgroundColor,
    secondaryBGColor,
    fontColor,
    fontSize,
    fontWeight,
    firstRowBGColor,
    firstColBGColor,
    firstRowFontColor,
    firstRowFontSize,
    firstRowFontWeight,
    firstColFontColor,
    firstColFontSize,
    firstColFontWeight,
    gridColor,
    gridType,
    horizontalAlign = "start",
    verticalAlign = "start",
    firstRowHorizontalAlign = "start",
    firstRowVerticalAlign = "start",
    firstColHorizontalAlign = "start",
    firstColVerticalAlign = "start",
  } = styled;

  let style = {};

  const getFontWeight = (value) =>
    value !== 1 ? (value === 0 ? 700 : 300) : null;

  const getDefaultBgColor = () => {
    if (tableStyle === 0) {
      return backgroundColor || null;
    } else if (tableStyle === 1) {
      return rowIndex % 2 === 0
        ? backgroundColor || null
        : secondaryBGColor || null;
    } else if (tableStyle === 2) {
      return cellIndex % 2 === 0
        ? backgroundColor || null
        : secondaryBGColor || null;
    }
    return null;
  };

  if (rowIndex === 0) {
    // First row
    if (firstRowBGColor) {
      style.backgroundColor = firstRowBGColor;
    } else if (cellIndex === 0 && firstColBGColor) {
      style.backgroundColor = firstColBGColor;
    } else {
      const bg = getDefaultBgColor();
      if (bg) style.backgroundColor = bg;
    }

    if (firstRowFontColor) style.color = firstRowFontColor;
    else if (fontColor) style.color = fontColor;

    if (firstRowFontSize) style.fontSize = firstRowFontSize;
    else if (fontSize) style.fontSize = fontSize;

    const fw = getFontWeight(
      firstRowFontWeight !== 1 ? firstRowFontWeight : fontWeight,
    );
    if (fw) style.fontWeight = fw;

    style.textAlign = firstRowHorizontalAlign;
    style.justifyContent = firstRowHorizontalAlign;
    style.alignItems = firstRowVerticalAlign;
  } else if (cellIndex === 0) {
    // First column
    if (firstColBGColor) {
      style.backgroundColor = firstColBGColor;
    } else {
      const bg = getDefaultBgColor();
      if (bg) style.backgroundColor = bg;
    }

    if (firstColFontColor) style.color = firstColFontColor;
    else if (fontColor) style.color = fontColor;

    if (firstColFontSize) style.fontSize = firstColFontSize;
    else if (fontSize) style.fontSize = fontSize;

    const fw = getFontWeight(
      firstColFontWeight !== 1 ? firstColFontWeight : fontWeight,
    );
    if (fw) style.fontWeight = fw;

    style.textAlign = firstColHorizontalAlign;
    style.justifyContent = firstColHorizontalAlign;
    style.alignItems = firstColVerticalAlign;
  } else {
    // Body cells
    const bg = getDefaultBgColor();
    if (bg) style.backgroundColor = bg;
    if (fontColor) style.color = fontColor;
    if (fontSize) style.fontSize = fontSize;

    const fw = getFontWeight(fontWeight);
    if (fw) style.fontWeight = fw;

    style.textAlign = horizontalAlign;
    style.justifyContent = horizontalAlign;
    style.alignItems = verticalAlign;
  }

  // Grid borders
  const gridStyle = `1px solid ${gridColor}`;
  if (gridType === 1) {
    style.borderTop = gridStyle;
    style.borderLeft = gridStyle;
    if (cellIndex === cellCount - 1) style.borderRight = gridStyle;
    if (rowIndex === rowCount - 1) style.borderBottom = gridStyle;
  } else if (gridType === 2) {
    style.borderBottom = gridStyle;
    if (rowIndex === 0) style.borderTop = gridStyle;
  } else if (gridType === 3) {
    style.borderRight = gridStyle;
    if (cellIndex === 0) style.borderLeft = gridStyle;
  }

  return style;
};
